import { useQuery } from '@apollo/client';
import { useHubble } from '@seek/hubble';
import { useCallback, useMemo, useState } from 'react';

import {
  CareerFeedComponent,
  GetCareerFeedDocument,
  type CareerFeedJob,
  type GetCareerFeedQueryVariables,
} from 'src/graphql/graphql';
import { useTimezone } from 'src/hooks/useTimezone';
import { logger } from 'src/modules/logger';
import {
  useCareerFeedJobAiSummariesEnabled,
  useIsCareerFeedCompanyEvpEnabled,
  useIsHomepageGenAIChatKillSwitchEnabled,
} from 'src/store/featureFlags/hooks';
import { useSelector } from 'src/store/react';
import {
  selectHasInitialSolIdCookie,
  selectServerAuthenticated,
  selectSolId,
} from 'src/store/selectors';
import type { ApolloCacheType } from 'src/types/apollo';
import { useMelwaysLocale, useMelwaysZone } from 'src/utils/melwaysHelper';

import { useCareerFeedAIAssistant } from '../CareerFeedAIAssistant/hooks/useCareerFeedAIAssistant';

import { mapQueryToCareerFeedItem } from './feed-types';
import { useReflectSSRAuthStatusFromError } from './useReflectSSRAuthStatusFromError';
type RefreshReturnType =
  | { success: true; jobsCount: number }
  | { success: false; error: Error };

export const useVisitorId = () => {
  const serverVisitorId = useSelector(selectSolId);
  const hasInitialSolId = useSelector(selectHasInitialSolIdCookie);
  const clientVisitorId = useHubble().visitorId();

  if (ENV.SERVER) {
    return hasInitialSolId ? serverVisitorId : undefined;
  }

  return clientVisitorId;
};

const useCareerFeedComponents = () => {
  const isCareerFeedCompanyEvpEnabled = useIsCareerFeedCompanyEvpEnabled();
  return useMemo(() => {
    const components: CareerFeedComponent[] = [CareerFeedComponent.Job];
    if (isCareerFeedCompanyEvpEnabled) {
      components.push(CareerFeedComponent.CompanyEvp);
    }
    return components;
  }, [isCareerFeedCompanyEvpEnabled]);
};

export const useCareerFeed = () => {
  const locale = useMelwaysLocale();
  const zone = useMelwaysZone();
  const visitorId = useVisitorId();
  const isServerAuthenticated = useSelector(selectServerAuthenticated);
  const timezone = useTimezone();
  const [isRefreshLoading, setRefreshLoading] = useState(false);
  const { persistedCandidateChatContext } = useCareerFeedAIAssistant();
  // This will make suer that the initial chat context is only used once, and the recs will not be updated when the context changes
  // When the context changed, we will call the `refresh()` separately to get the new recs on the AI Assistant
  const [initialChatContext] = useState(persistedCandidateChatContext ?? null);
  const isHomepageGenAIChatKillSwitchEnabled =
    useIsHomepageGenAIChatKillSwitchEnabled();
  const ssr = isServerAuthenticated && Boolean(visitorId);
  const enableJobAiSummaries = useCareerFeedJobAiSummariesEnabled();

  const { data, error, loading, refetch } = useQuery(GetCareerFeedDocument, {
    errorPolicy: 'all',
    variables: {
      locale,
      zone,
      timezone,
      visitorId,
      components: useCareerFeedComponents(),
      candidateChatContext: isHomepageGenAIChatKillSwitchEnabled
        ? null
        : initialChatContext,
      enableJobAiSummaries,
    },
    ssr,
  });

  useReflectSSRAuthStatusFromError({ error, action: 'useCareerFeed' });

  const feedItems = useMemo(() => mapQueryToCareerFeedItem(data), [data]);

  const refresh = useCallback(
    async (
      args?: Partial<GetCareerFeedQueryVariables>,
    ): Promise<RefreshReturnType> => {
      setRefreshLoading(true);
      try {
        const result = await refetch(args);
        setRefreshLoading(false);

        if (result.error) {
          return {
            success: false,
            error: result.error,
          };
        }

        return {
          success: true,
          jobsCount: result.data.careerFeed?.edges?.length ?? 0,
        };
      } catch (e) {
        logger.error(
          {
            err: e as Error,
          },
          'Error while refreshing with CareerFeed API',
        );
        setRefreshLoading(false);
        return {
          success: false,
          error: e as Error,
        };
      }
    },
    [refetch],
  );

  return {
    refresh,
    feedItems,
    error,
    loading: loading || isRefreshLoading,
    isRefreshLoading,
    tracking: {
      solMetadata: data?.careerFeed?.solMetadata,
      serviceToken: data?.careerFeed?.serviceToken,
    },
  };
};

export const useUpdateCareerFeedSavedJobCache = () => (jobId?: string) => {
  if (!jobId) {
    return undefined;
  }

  const update = (isSaved: boolean) => (cache: ApolloCacheType) => {
    const targetJob = cache.identify({
      jobId,
      __typename: 'CareerFeedJob',
    });

    if (!targetJob) {
      return;
    }

    cache.modify<CareerFeedJob>({
      id: targetJob,
      fields: {
        savedJob() {
          return isSaved ? { id: jobId } : null;
        },
      },
    });
  };

  return { setSaved: update(true), setUnsave: update(false) };
};
